import React, { useState, useEffect } from 'react';
import { compose } from 'ramda';
import { reduxForm, InjectedFormProps, Field, Form } from 'redux-form';
import { makeStyles } from '@material-ui/core/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import axios from 'axios';

import Button from 'components/Button/Button';
import Fields from 'components/Fields';
import IntlProvider, {
  useFormatMessage,
  IntlProviderType,
} from 'components/IntlProvider';

import workbenches from 'api/resources/products/workbenches/workbenches';
import briefings from 'api/resources/products/briefings/briefings';

import { BriefingType } from 'api/resources/products/briefings/briefings.types';
import { WorkbenchType } from 'api/resources/products/workbenches/workbenches.types';

const useStyles = makeStyles((theme) => ({
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  small: {
    fontSize: '0.875rem',
    fontWeight: 700,
  },
  link: {
    '&:hover': {
      opacity: 0.6,
    },
  },
}));

interface ProductInfoProps extends InjectedFormProps {
  intl: IntlProviderType;
  initialValues: {
    isEditMode?: boolean;
    products: { [k: string]: any }[];
  };
}

const ProductInfo = ({ handleSubmit, error, submitting }: ProductInfoProps) => {
  const classes = useStyles();
  const t = useFormatMessage();
  const [workbenchesArray, setWorkbenchesArray] = useState([]);
  const [briefingsArray, setBriefingsArray] = useState([]);

  useEffect(() => {
    axios.all([workbenches.getWorkbenches(), briefings.getBriefings()]).then(
      axios.spread((workbenchesRes, briefingsRes) => {
        setWorkbenchesArray(workbenchesRes.data.results);
        setBriefingsArray(briefingsRes.data.results);
      })
    );
  }, []);

  return (
    <Form onSubmit={handleSubmit} className={classes.form} noValidate>
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.name', 'Product name')}
        name="name"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.slug', 'Product slug')}
        name="slug"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.description', 'Product description')}
        name="description"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Select}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.briefing', 'Select briefing template')}
        name="briefing_template"
        InputLabelProps={{
          shrink: true,
        }}
        options={briefingsArray.map((item: BriefingType) => {
          return { value: item, label: item.name };
        })}
      />
      <Field
        component={Fields.Select}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.workbench', 'Select workbench')}
        name="workbench_id"
        InputLabelProps={{
          shrink: true,
        }}
        options={workbenchesArray.map((item: WorkbenchType) => {
          return { value: item.id, label: item.name };
        })}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.keywords', 'Product keywords')}
        name="keywords"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.meta_description', 'Meta description')}
        name="meta_description"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.title', 'Product title')}
        name="title"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.subtitle', 'Product subtitle')}
        name="subtitle"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Text}
        variant="outlined"
        margin="normal"
        fullWidth
        label={t('forms.product.cta_text', 'Product CTA')}
        name="cta_text"
        InputLabelProps={{
          shrink: true,
        }}
      />
      <Field
        component={Fields.Checkbox}
        type="checkbox"
        name="active"
        label={t('forms.workbench.active', 'Do you want it to be active?')}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        isLoading={submitting}
        className={classes.submit}
      >
        {t('forms.profile.save', 'Save')}
      </Button>
    </Form>
  );
};

export default compose(
  IntlProvider.withIntl,
  reduxForm({
    form: 'workbenchInfo',
  })
)(ProductInfo);
